import { useWatch } from 'react-hook-form';
import { Alert } from '@skupreme/shared/ui';
import {
  FormCheckbox,
  FormPasswordField,
  FormTextField,
} from '@skupreme/shared/ui/form';
import { CommonUtility } from '@utility/common';
import { contactHelpFulfillmentLink } from '@utility/constants/common';
import { IntegrationType } from '../../lib/integration';
import { QuantityTypeField } from './QuantityTypeField';
import type { StepProps } from './types';

type Brand = {
  name: string;
  link: string;
  logo: string;
  /** Each logo has its own proportions; this keeps them about one size. */
  logoHeight: number;
  userLabel: string;
  userPlaceholder?: string;
};

const TACTICAL: Brand = {
  name: 'Tactical Logistics',
  link: contactHelpFulfillmentLink.tacticalLogistics,
  logo: '/images/tactical/logo-tactical.svg',
  logoHeight: 57,
  userLabel: 'Email Address',
};

const BRANDS: Record<string, Brand> = {
  [IntegrationType.tactical_logistics]: TACTICAL,
  [IntegrationType.mintsoft]: {
    name: 'Mintsoft',
    link: contactHelpFulfillmentLink.mintsoft,
    logo: CommonUtility.getCdnLink('integrations/mintsoft.png'),
    logoHeight: 100,
    userLabel: 'User Name',
  },
  [IntegrationType.santaFe]: {
    name: 'Santa Fe Fulfillment',
    link: contactHelpFulfillmentLink.santaFe,
    logo: CommonUtility.getCdnLink('integrations/sff.png'),
    logoHeight: 57,
    userLabel: 'Name',
    userPlaceholder: 'FtpUserName',
  },
  [IntegrationType.camelot]: {
    name: 'Camelot 3PL',
    link: contactHelpFulfillmentLink.camelot,
    logo: CommonUtility.getCdnLink('integrations/camelot.png'),
    logoHeight: 90,
    userLabel: 'Name',
    userPlaceholder: 'FtpUserName',
  },
  [IntegrationType.shipHero]: {
    name: 'ShipHero',
    link: contactHelpFulfillmentLink.shipHero,
    logo: CommonUtility.getCdnLink('integrations/ship_hero.png'),
    logoHeight: 70,
    userLabel: 'Refresh Token',
  },
  [IntegrationType.skudrop]: {
    name: 'SKUdrop',
    link: contactHelpFulfillmentLink.SKUdrop,
    logo: CommonUtility.getCdnLink('integrations/skudrop.png'),
    logoHeight: 57,
    userLabel: 'Api Key',
  },
  [IntegrationType.goodCang]: {
    name: 'GOODCANG',
    link: contactHelpFulfillmentLink.goodCang,
    logo: CommonUtility.getCdnLink('integrations/goodcang_logo.png'),
    logoHeight: 57,
    userLabel: 'Api Key',
  },
};

type CredentialsStepProps = StepProps & { integrationType?: string };

export function CredentialsStep({
  control,
  fieldError,
  isVisible,
  integrationType = '',
}: CredentialsStepProps) {
  const fulfillmentServices = useWatch({
    control,
    name: 'fulfillment_services',
  });
  const brand = BRANDS[integrationType] ?? TACTICAL;
  const is = (type: string) => integrationType === type;
  const isTactical = is(IntegrationType.tactical_logistics);
  const hasHost =
    is(IntegrationType.santaFe) ||
    is(IntegrationType.goodCang) ||
    is(IntegrationType.camelot);
  const hasPassword = !(
    is(IntegrationType.goodCang) ||
    is(IntegrationType.shipHero) ||
    is(IntegrationType.skudrop)
  );

  return (
    <div className={isVisible ? 'sk:flex sk:flex-col sk:gap-4' : 'sk:hidden'}>
      <div className="sk:flex sk:flex-col sk:items-center sk:gap-4 sk:pt-2">
        <img
          alt={brand.name}
          src={brand.logo}
          height={brand.logoHeight}
          width={230}
          className="sk:object-contain"
        />
        <p className="sk:m-0 sk:text-dense sk:text-fg-secondary">
          Please contact{' '}
          <a href={brand.link} target="_blank" rel="noreferrer">
            {brand.name}
          </a>{' '}
          for access or to set up your 3PL.
        </p>
      </div>
      {hasHost ? (
        <FormTextField
          control={control}
          name="host"
          label={is(IntegrationType.goodCang) ? 'App Token' : 'Host'}
          placeholder={
            is(IntegrationType.goodCang) ? 'Token' : 'IP:OR:HOSTNAME'
          }
          error={fieldError('host')}
          isRequired={isVisible}
        />
      ) : null}
      <FormTextField
        control={control}
        name="username"
        label={brand.userLabel}
        placeholder={brand.userPlaceholder ?? brand.userLabel}
        error={fieldError('username')}
        isRequired={isVisible}
      />
      {hasPassword ? (
        <FormPasswordField
          control={control}
          name="password"
          label="Password"
          error={fieldError('password')}
          isRequired={isVisible}
        />
      ) : null}
      {isTactical ? (
        <>
          <FormCheckbox
            control={control}
            name="fulfillment_services"
            label="Fulfillment Services"
          />
          {fulfillmentServices ? (
            <Alert tone="warning">
              Please confirm with your Tactical Logistics contact that your
              credentials are configured for fulfillment services.
            </Alert>
          ) : null}
        </>
      ) : null}
      {is(IntegrationType.camelot) ? (
        <>
          <FormTextField
            control={control}
            name="depositorCode"
            label="Customer Code"
            error={fieldError('depositorCode')}
            isRequired={isVisible}
          />
          <FormTextField
            control={control}
            name="homeDirectory"
            label="Inventory Directory"
            error={fieldError('homeDirectory')}
            isRequired={isVisible}
          />
          <FormTextField
            control={control}
            name="homeDirectoryShipments"
            label="Shipment Directory"
            error={fieldError('homeDirectoryShipments')}
            isRequired={isVisible}
          />
        </>
      ) : null}
      {isTactical ? null : <QuantityTypeField control={control} />}
    </div>
  );
}
